import React, { useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import { MapPin } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icons in Leaflet with React 18
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

// Custom marker icons for different status
const createCustomIcon = (status) => {
  const color = status === 'active' ? '#10b981' : status === 'recent' ? '#f59e0b' : '#6b7280';
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="background-color: ${color}; width: 32px; height: 32px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center;">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
      </svg>
    </div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32]
  });
};

const WorkersLocationMap = ({ workers = [] }) => {
  const workersWithLocation = useMemo(() => 
    workers.filter(w => w.latitude && w.longitude),
    [workers]
  );

  const center = useMemo(() => {
    // Always center on Mbombela area
    return [-25.4700, 31.0200]; // Mbombela
  }, []);

  // Calculate status counts
  const statusCounts = useMemo(() => {
    const counts = { active: 0, recent: 0, offline: 0 };
    workersWithLocation.forEach(worker => {
      const lastUpdate = worker.last_location_time ? new Date(worker.last_location_time) : null;
      const minutesAgo = lastUpdate ? Math.floor((new Date() - lastUpdate) / 60000) : null;
      
      if (minutesAgo < 30) counts.active++;
      else if (minutesAgo < 120) counts.recent++;
      else counts.offline++;
    });
    return counts;
  }, [workersWithLocation]);

  if (workersWithLocation.length === 0) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50">
        <div className="text-center text-gray-500">
          <MapPin className="h-16 w-16 mx-auto mb-3 text-gray-400" />
          <p className="text-sm font-medium">No location data available</p>
          <p className="text-xs mt-1">Waiting for artisans to share their location</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full">
      <MapContainer
        center={center}
        zoom={11}
        style={{ height: '100%', width: '100%' }}
        className="z-0"
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://carto.com/">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
          maxZoom={19}
          subdomains={['a', 'b', 'c', 'd']}
        />
        {workersWithLocation.map((worker) => {
          const lastUpdate = worker.last_location_time 
            ? new Date(worker.last_location_time)
            : null;
          const minutesAgo = lastUpdate 
            ? Math.floor((new Date() - lastUpdate) / 60000)
            : null;
          
          const status = minutesAgo < 30 ? 'active' : minutesAgo < 120 ? 'recent' : 'offline';
          const accuracyRadius = worker.accuracy || 50;

          return (
            <React.Fragment key={worker.worker_id}>
              {/* Accuracy circle */}
              <Circle
                center={[parseFloat(worker.latitude), parseFloat(worker.longitude)]}
                radius={accuracyRadius}
                pathOptions={{
                  color: status === 'active' ? '#10b981' : status === 'recent' ? '#f59e0b' : '#6b7280',
                  fillColor: status === 'active' ? '#10b981' : status === 'recent' ? '#f59e0b' : '#6b7280',
                  fillOpacity: 0.1,
                  weight: 1
                }}
              />
              
              {/* Marker */}
              <Marker 
                position={[parseFloat(worker.latitude), parseFloat(worker.longitude)]}
                icon={createCustomIcon(status)}
              >
                <Popup className="custom-popup">
                  <div className="p-2 min-w-[250px]">
                    <div className="flex items-center gap-2 mb-2 pb-2 border-b">
                      <div className={`w-3 h-3 rounded-full ${
                        status === 'active' ? 'bg-green-500 animate-pulse' :
                        status === 'recent' ? 'bg-yellow-500' :
                        'bg-gray-500'
                      }`} />
                      <h4 className="font-bold text-gray-900">{worker.worker_name}</h4>
                    </div>
                    
                    <div className="space-y-1 text-xs text-gray-700 mb-3">
                      <div className="flex justify-between">
                        <span className="font-medium">Employee ID:</span>
                        <span>{worker.worker_employee_id || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium">Phone:</span>
                        <span>{worker.worker_phone || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium">Coordinates:</span>
                        <span className="text-blue-600">
                          {parseFloat(worker.latitude).toFixed(5)}, {parseFloat(worker.longitude).toFixed(5)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium">Accuracy:</span>
                        <span>±{Math.round(accuracyRadius)}m</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium">Last Update:</span>
                        <span className={
                          status === 'active' ? 'text-green-600 font-medium' :
                          status === 'recent' ? 'text-yellow-600' :
                          'text-gray-500'
                        }>
                          {minutesAgo < 1 ? 'Just now' :
                           minutesAgo < 60 ? `${minutesAgo}m ago` :
                           minutesAgo < 1440 ? `${Math.floor(minutesAgo / 60)}h ago` :
                           `${Math.floor(minutesAgo / 1440)}d ago`}
                        </span>
                      </div>
                    </div>

                    <a
                      href={`https://www.google.com/maps?q=${worker.latitude},${worker.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full py-1.5 px-3 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded text-center transition-colors"
                    >
                      Open in Google Maps →
                    </a>
                  </div>
                </Popup>
              </Marker>
            </React.Fragment>
          );
        })}
      </MapContainer>
      
      {/* Custom Overlay - Legend and Counter */}
      <div className="absolute top-4 right-4 z-[1000] bg-white rounded-lg shadow-lg border border-gray-200 p-3">
        <div className="space-y-2">
          {/* Counter */}
          <div className="flex items-center justify-center pb-2 border-b border-gray-200">
            <MapPin className="h-4 w-4 mr-1.5 text-blue-600" />
            <span className="text-sm font-bold text-blue-600">
              {workersWithLocation.length} on Map
            </span>
          </div>
          
          {/* Legend */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="text-xs text-gray-700">Active (&lt;30m)</span>
              </div>
              <span className="text-xs font-semibold text-gray-900">{statusCounts.active}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <span className="text-xs text-gray-700">Recent (&lt;2h)</span>
              </div>
              <span className="text-xs font-semibold text-gray-900">{statusCounts.recent}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-gray-500"></div>
                <span className="text-xs text-gray-700">Offline</span>
              </div>
              <span className="text-xs font-semibold text-gray-900">{statusCounts.offline}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkersLocationMap;
